import styled from 'styled-components';
import { BtnBlue, BtnGhost, BtnRed } from './Button';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IAuth } from 'customTypes';
import RadioContainer from './button/RadioContainer';
import Portal from './Portal';
import YesOrNoConfirm from './Modal/YesOrNo';
import TextareaAutosize from 'react-textarea-autosize';
import HistorySettings from '../pages/sm_setting/setting12/HistorySettings';
import TextInfoBox from './TextInfoBox';

const ButtonsWrapper = styled.div`
  border-top: 1px solid var(--outline);
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
  padding: 0.5rem;
  div {
    display: flex;
    gap: 0.5rem;
    &.disabled {
      opacity: 0.5;
      pointer-events: none;
      cursor: not-allowed;
    }
    button {
      height: 2.5rem;
      font-size: 0.875rem;
    }
  }
`;

const Root = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: fit-content;
  overflow: hidden;
  justify-content: flex-start;
  .header {
    padding: 0.75rem;
    height: 3rem;
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    color: var(--text-secondary);
    border-bottom: 1px solid var(--outline);
    display: flex;
    gap: 0.5rem;
    justify-content: space-between;
    .title {
      flex-shrink: 0;
      width: fit-content;
      font-weight: 600;
      margin-right: 0.5rem;
    }
    input {
      font-size: 0.875rem;
      color: var(--text-secondary);
      background-color: var(--tonal-light);
      border: 1px solid var(--outline);
    }
  }
  .body {
    flex-grow: 1;
    overflow: hidden;
    padding: 0.5rem;
    .textareaTitle {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 0.875rem;
      padding: 0.25rem;
      color: var(--text-tertiary);
    }
    .inputLabel {
      color: var(--text-secondary);
      font-weight: 600;
      font-size: 0.875rem;
    }
    .editor {
      width: 100%;
      border: 1px solid var(--outline);
      border-radius: 0.25rem;
      padding: 0.5rem 0.75rem;
      padding: 1rem 0.75rem;
      resize: none;
      font-size: 1rem;
      font-size: 0.875rem;
      color: var(--text-secondary);
      background-color: var(--tonal-light);
      :focus {
        outline: 0.125rem solid var(--filled-sky);
        outline: 2px solid #3699ff;
      }
    }
    .bigo {
      margin-top: 0.5rem;
    }
    input {
      font-size: 0.875rem;
      color: var(--text-tertiary);
      background-color: var(--tonal-light);
      border: 1px solid var(--outline);
    }
    .history {
      margin-top: 1.5rem;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem 0.75rem;
    }
  }
`;

interface IFormEditor {
  auth: IAuth;
  selectedIndex: number | null;
  setSelectedIndex: Dispatch<SetStateAction<number | null>>;
  data: any[];
  refetch: () => void;
  setWarning: Dispatch<SetStateAction<boolean>>;
  onSave?: (formData: any) => void;
  onDelete?: (item: any) => void;
  onReset?: () => void;
  titleLabel?: string;
  contentLabel?: string;
  noteLabel?: string;
  maxTitleLength?: number;
  maxContentLength?: number;
  maxNoteLength?: number;
  onUseYnChange?: (item: any, useYn: boolean) => void;
}

const FormEditor = ({
  auth,
  selectedIndex,
  setSelectedIndex,
  data,
  refetch,
  setWarning,
  onSave,
  onDelete,
  onReset,
  titleLabel = '제목',
  contentLabel = '내용',
  noteLabel = '비고',
  maxTitleLength = 50,
  maxContentLength = 500,
  maxNoteLength = 100,
  onUseYnChange,
}: IFormEditor) => {
  const { t } = useTranslation();
  const initInputs = { title: '', contents: '', bigo: '', useYn: 'N' };
  const [inputs, setInputs] = useState<Record<string, string>>(initInputs);
  const [openModal, setOpenModal] = useState<{ status: boolean; type: string }>({
    status: false,
    type: 'confirm',
  });

  // 목록에서 선택한 행을 form data에 적용
  useEffect(() => {
    if (selectedIndex !== null && data[selectedIndex]) {
      setInputs({
        title: data[selectedIndex].title,
        contents: data[selectedIndex].contents,
        useYn: data[selectedIndex].useYn,
        bigo: data[selectedIndex].bigo,
      });
    } else {
      setInputs(initInputs);
    }
  }, [selectedIndex, data]);

  // 알림톡 저장 버튼 활성화 여부 체크
  useEffect(() => {
    setWarning(!isDisabledInit());
  }, [inputs]);

  // 알림톡 내용 수정 함수
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.currentTarget;
    setInputs((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // 방문자 서약서 삭제 버튼 클릭 함수
  const onClickDelete = async () => {
    if (selectedIndex === null) {
      setInputs(initInputs);
    } else {
      const requestData = data[selectedIndex];
      delete requestData.writer;
      delete requestData.wDate;
      delete requestData.editor;
      delete requestData.eDate;
      onSave?.({ ...requestData, useYn: 'N', delYn: 'Y' });
    }
  };

  // 저장 버튼 클릭 함수
  const onClickSave = async () => {
    const formData = {
      title: inputs.title,
      contents: inputs.contents,
      bigo: inputs.bigo,
      useYn: inputs.useYn,
      delYn: 'N',
      ...(selectedIndex !== null && {
        id: data[selectedIndex].id,
        writer: data[selectedIndex].writer,
      }),
    };

    onSave?.(formData);
    // 저장하려는 서약서의 사용유무가 'Y'인 경우
    // 기존 '사용'인 서약서의 사용유무를 'N'으로 변경
    // (onUseYnChange에서 사용유무를 토글로 전환하므로 사용유무를 직접 반대값으로 변경하여 전달)
    if (inputs.useYn === 'Y') onUseYnChange?.({ ...formData, useYn: 'N' }, false);
  };

  const isDisabledInit = () => {
    if (selectedIndex !== null && data[selectedIndex]) {
      const comparison =
        inputs.title === data[selectedIndex].title && inputs.contents === data[selectedIndex].contents && inputs.bigo === data[selectedIndex].bigo && inputs.useYn === data[selectedIndex].useYn;
      return comparison;
    }
    const comparison = inputs.title.length === 0 || inputs.contents.length === 0;
    return comparison || (inputs.title === '' && inputs.contents === '' && inputs.useYn === 'N');
  };

  return (
    <Root>
      <div className='header'>
        <span className='title'>{t(titleLabel)}</span>
        <input type='text' id='title' name='title' value={inputs.title} onChange={(e) => onChange(e)} disabled={!auth.createAuth && !auth.updateAuth} maxLength={maxTitleLength} />
        <RadioContainer name={t('사용')} onClick={() => setInputs({ ...inputs, useYn: 'Y' })} isSelected={inputs.useYn === 'Y'} />
        <RadioContainer name={t('미사용')} onClick={() => setInputs({ ...inputs, useYn: 'N' })} isSelected={inputs.useYn === 'N'} />
      </div>
      {selectedIndex === null && <TextInfoBox ico='flash_on' description='제목과 내용을 모두 입력해주세요.' color='orange' />}
      <div className='body'>
        <div className='textareaTitle'>
          <span className='inputLabel'>{t(contentLabel)}</span>
          <span>{`${t('글자 수')} (${inputs.contents.length}/${maxContentLength})`}</span>
        </div>
        <TextareaAutosize
          className='editor'
          maxLength={maxContentLength}
          minRows={8}
          maxRows={16}
          name={inputs.contents}
          onChange={(e) => setInputs({ ...inputs, contents: e.target.value })}
          value={inputs.contents}
        />
        <div className='bigo'>
          <span className='inputLabel'>{t(noteLabel)}</span>
          <input type='text' id='bigo' name='bigo' value={inputs.bigo} onChange={(e) => onChange(e)} disabled={!auth.createAuth && !auth.updateAuth} maxLength={maxNoteLength} />
        </div>
        {selectedIndex !== null && (
          <div className='history'>
            <HistorySettings data={data[selectedIndex]?.wDate} labelName={t('등록일시')} />
            <HistorySettings data={data[selectedIndex]?.writer} labelName={t('등록자')} />
            <HistorySettings data={data[selectedIndex]?.eDate} labelName={t('수정일시')} />
            <HistorySettings data={data[selectedIndex]?.editor} labelName={t('수정자')} />
          </div>
        )}
      </div>
      <ButtonsWrapper>
        <div>{selectedIndex !== null && auth.deleteAuth && <BtnRed onClick={() => setOpenModal({ status: true, type: 'confirm' })}>{t('삭제')}</BtnRed>}</div>
        <div className={isDisabledInit() ? 'disabled' : ''}>
          <BtnGhost
            onClick={() => {
              if (selectedIndex === null) {
                setSelectedIndex(null);
                setInputs(initInputs);
              } else {
                setInputs({
                  title: data[selectedIndex].title,
                  contents: data[selectedIndex].contents,
                  useYn: data[selectedIndex].useYn,
                  bigo: data[selectedIndex].bigo,
                });
              }
              onReset?.();
            }}
          >
            {t('초기화')}
          </BtnGhost>
          {(auth.createAuth || auth.updateAuth) && <BtnBlue onClick={onClickSave}>{t('저장')}</BtnBlue>}
        </div>
      </ButtonsWrapper>
      <Portal openModal={openModal?.status}>
        {openModal && openModal.type === 'confirm' && (
          <YesOrNoConfirm
            mainText={t('정말 삭제하시겠습니까?')}
            onClickClose={() => setOpenModal({ status: false, type: 'confirm' })}
            onClickConfirm={() => {
              onClickDelete();
              setOpenModal({ status: false, type: 'confirm' });
            }}
          />
        )}
      </Portal>
    </Root>
  );
};

export default FormEditor;
